import type { SectionId } from './reportEnums.constants';
import { buildConnectionsBhCareInsights } from './connectionsBhCareInsights.util';
import { VBP_ENGAGEMENT_INSIGHTS } from './vbpEngagementInsights.util';
import {
  buildHeroKpiSubcopy,
  HERO_KPI_LABEL_ENGAGEMENT_RATE,
  HERO_KPI_LABEL_MEMBERS_ENGAGED,
  HERO_KPI_LABEL_NEEDS_FULFILLED,
  HERO_KPI_LABEL_SATISFACTION_SCORE,
} from './heroKpiSnapshot.util';
import { buildHeroTagline } from './reportMeasurementPeriod.util';
import {
  ENGAGEMENT_FUNNEL_ELIGIBLE_SUBCOPY,
  formatEngagementFunnelEngagedSubCopy,
} from './engagementFunnelCopy.util';
import {
  buildWhatsPossibleAtMaturityCopy,
  buildWhatsPossibleAtMaturityMetrics,
} from './whatsPossibleAtMaturitySnapshot.util';
import {
  buildLookingAheadSnapshot,
  mockLookingAheadEngagementRatePct,
} from './lookingAheadSnapshot.util';
import { QUALITY_MEASURE_PROJECTION_CARDS } from './qualityMeasureProjections.constants';
import {
  makeRng,
  type MockSectionCustomerInput,
} from './mockSectionCustomer.util';
import { defaultMemberSpotlightSnapshot } from './memberSpotlightSnapshot.util';

export type { MockSectionCustomerInput } from './mockSectionCustomer.util';
export { makeRng } from './mockSectionCustomer.util';

function intBetween(rng: () => number, min: number, max: number): number {
  return Math.floor(min + rng() * (max - min + 1));
}

/**
 * Deterministic mock section data (v1). Keyed by
 * (customerId, reportingPeriod, sectionId) for stable preview snapshots.
 *
 * @param sectionId Library section identifier.
 * @param customer Customer context for snapshot generation.
 * @param reportingPeriod Human reporting period label.
 * @returns JSON snapshot for the section renderer.
 */
export function mockSectionData(
  sectionId: SectionId,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> {
  const rng = makeRng(customer.id, reportingPeriod, sectionId);

  switch (sectionId) {
    case "hero": {
      const engaged = intBetween(rng, 900, 1400);
      const engagementRate = Math.round((0.45 + rng() * 0.1) * 100);
      const needsFulfilledRate = Math.round((0.4 + rng() * 0.15) * 100);
      const satisfactionWhole = intBetween(rng, 4, 5);
      const satisfaction =
        rng() > 0.5
          ? `${satisfactionWhole} / 5`
          : `${(satisfactionWhole - 0.5 + rng() * 0.4).toFixed(1)} / 5`;
      return {
        customerName: customer.name,
        reportingPeriod,
        payer: 'Other',
        headline:
          "Your hardest-to-reach members are getting the help they need.",
        tagline: buildHeroTagline(customer.name, reportingPeriod),
        kpiTiles: [
          {
            label: HERO_KPI_LABEL_MEMBERS_ENGAGED,
            value: engaged.toLocaleString(),
            sub: buildHeroKpiSubcopy(
              HERO_KPI_LABEL_MEMBERS_ENGAGED,
              customer.name,
            ),
          },
          {
            label: HERO_KPI_LABEL_ENGAGEMENT_RATE,
            value: `${engagementRate}%`,
            sub: buildHeroKpiSubcopy(
              HERO_KPI_LABEL_ENGAGEMENT_RATE,
              customer.name,
            ),
          },
          {
            label: HERO_KPI_LABEL_NEEDS_FULFILLED,
            value: `${needsFulfilledRate}%`,
            sub: buildHeroKpiSubcopy(
              HERO_KPI_LABEL_NEEDS_FULFILLED,
              customer.name,
            ),
          },
          {
            label: HERO_KPI_LABEL_SATISFACTION_SCORE,
            value: satisfaction,
            sub: buildHeroKpiSubcopy(
              HERO_KPI_LABEL_SATISFACTION_SCORE,
              customer.name,
            ),
          },
        ],
      };
    }

    case "engagement-story": {
      const eligible = intBetween(rng, 8000, 24000);
      const interacted = Math.round(eligible * (0.35 + rng() * 0.12));
      const engaged = Math.round(interacted * (0.55 + rng() * 0.2));
      const engagementRate = Math.round((engaged / eligible) * 100);
      const conversionRate =
        interacted > 0 ? Math.round((engaged / interacted) * 100) : 0;
      const monthLabels = ["Sep", "Oct", "Nov", "Dec", "Jan", "Feb"];
      const trend = monthLabels.map((m, i) => {
        const growthEngaged = 0.55 + i * 0.08 + rng() * 0.03;
        const growthInteracted = 0.72 + i * 0.06 + rng() * 0.03;
        return {
          month: m,
          engaged: Math.round(engaged * growthEngaged),
          interacted: Math.round(interacted * growthInteracted),
        };
      });
      return {
        headline: "Your hardest-to-reach members are showing up",
        trend,
        funnel: [
          {
            label: "Ever Eligible",
            value: eligible,
            subCopy: ENGAGEMENT_FUNNEL_ELIGIBLE_SUBCOPY,
          },
          {
            label: "Ever Interacted",
            value: interacted,
            subCopy: "Members who responded — a call, text, or in person.",
          },
          {
            label: "Ever Engaged",
            value: engaged,
            subCopy: formatEngagementFunnelEngagedSubCopy(conversionRate),
          },
        ],
        denominators: [
          {
            label: "Engagement rate",
            value: `${engagementRate}%`,
            hint: `${engaged.toLocaleString()} of ${eligible.toLocaleString()} eligible`,
            variant: "engagement-rate",
          },
          {
            label: "Conversion rate",
            value: `${conversionRate}%`,
            hint: `${engaged.toLocaleString()} of ${interacted.toLocaleString()} interacted`,
            variant: "conversion-rate",
          },
        ],
        whyDenominator:
          "Engagement rate and conversion rate summarize how members move from eligibility to an active guide relationship.",
        insights: {
          clinical: {
            heading: "Clinical Insight",
            body: "Members reached through peer outreach show 2.1× higher follow-through on behavioral care referrals than industry averages for hard-to-reach populations.",
            citationLabel: "PMC — Peer support outcomes",
            citationUrl:
              "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6207970/",
          },
          financial: {
            heading: "Financial Insight",
            body: "Each additional 10% of engagement in a Medicaid population correlates with an estimated $18 PMPM reduction in avoidable utilization.",
            citationLabel: "Health Affairs — Engagement & TCoC",
            citationUrl:
              "https://www.healthaffairs.org/doi/10.1377/hlthaff.2019.00301",
          },
        },
      };
    }


    case "vbp-engagement": {
      const eligible = intBetween(rng, 2500, 5000);
      const assigned = Math.round(eligible * (0.42 + rng() * 0.12));
      const engaged = Math.round(assigned * (0.68 + rng() * 0.15));
      const growth = Array.from({ length: 6 }, (_, i) =>
        Math.round(engaged * (0.55 + i * 0.08 + rng() * 0.04)),
      );
      return {
        vbpEligible: eligible,
        vbpEngaged: engaged,
        engagementRatePct:
          eligible > 0 ? Math.round((engaged / eligible) * 100) : 0,
        funnel: [
          { label: "VBP eligible", value: eligible },
          { label: "VBP engaged", value: engaged },
        ],
        sixMonthGrowth: growth,
        insights: VBP_ENGAGEMENT_INSIGHTS,
      };
    }

    case "who-were-reaching": {
      const stateCode = (customer.usStateCode ?? "MI").toUpperCase();
      const miCounties = [
        { name: "Wayne", memberCount: intBetween(rng, 280, 480) },
        { name: "Oakland", memberCount: intBetween(rng, 120, 220) },
        { name: "Macomb", memberCount: intBetween(rng, 90, 180) },
        { name: "Genesee", memberCount: intBetween(rng, 60, 120) },
        { name: "Kent", memberCount: intBetween(rng, 50, 110) },
        { name: "Washtenaw", memberCount: intBetween(rng, 40, 100) },
      ];
      const engagedTotal = miCounties.reduce(
        (sum, row) => sum + row.memberCount,
        0,
      );
      return {
        usStateCode: stateCode,
        yearMo: 202605,
        engagedTotal,
        counties: miCounties,
        age: [
          { label: "18–34", pct: intBetween(rng, 18, 28) },
          { label: "35–54", pct: intBetween(rng, 30, 42) },
          { label: "55–64", pct: intBetween(rng, 18, 28) },
          { label: "65+", pct: intBetween(rng, 10, 20) },
        ],
        raceEthnicity: [
          { label: "Black / African American", pct: intBetween(rng, 28, 38) },
          { label: "White", pct: intBetween(rng, 30, 40) },
          { label: "Hispanic / Latino", pct: intBetween(rng, 12, 20) },
          { label: "Asian", pct: intBetween(rng, 4, 10) },
          { label: "Other / multiple", pct: intBetween(rng, 4, 10) },
        ],
      };
    }

    case "what-members-need": {
      const themes = [
        "Access to behavioral health",
        "Affordability of prescriptions",
        "Care navigation confusion",
        "Transportation to appointments",
        "Loneliness / isolation",
        "Housing instability",
        "Food security",
        "Chronic pain management",
      ];
      const shuffled = [...themes].sort(() => rng() - 0.5).slice(0, 5);
      const topNeeds = shuffled.map((label, i) => ({
        label,
        count: intBetween(rng, 120, 480) - i * 30,
      }));
      const quarters = ["Q3'25", "Q4'25", "Q1'26", "Q2'26"];
      const identifiedVsFulfilled = quarters.map((q, i) => {
        const identified = intBetween(rng, 800, 1400) + i * 120;
        const fulfilled = Math.round(identified * (0.55 + i * 0.05 + rng() * 0.05));
        return { label: q, identified, fulfilled };
      });
      return {
        topNeeds,
        identifiedVsFulfilled,
        insights: {
          clinical: {
            heading: "Members can't focus on health until the basics are met.",
            body: "Adults with unmet social needs are 84% more likely to postpone needed medical care and 116% more likely to postpone medications. Every need closed is a barrier removed from the path to clinical care your plan already covers.",
            citationLabel: "Citation",
            citationUrl: "https://pubmed.ncbi.nlm.nih.gov/16423128/",
          },
          financial: {
            heading: "Closed needs reduce your highest-cost utilization patterns.",
            body: "Food-insecure adults have 47% more ED visits, 47% more hospitalizations, and are nearly twice as likely to fall in the top 5% of healthcare expenditures. Closing those needs is cost containment.",
            citationLabel: "Citation",
            citationUrl:
              "https://www.ajmc.com/view/food-insecurity-healthcare-utilization-and-high-cost-a-longitudinal-cohort-study",
          },
        },
      };
    }

    case "connections-that-matter": {
      const cbos = [
        "United Way 211",
        "Focus: HOPE",
        "Gleaners Community Food Bank",
        "Wayne Metro CAA",
        "The Heat and Warmth Fund (THAW)",
        "Matrix Human Services",
      ];
      const gov = [
        "SNAP enrollment",
        "Medicaid recertification",
        "LIHEAP energy assistance",
        "WIC",
        "SSI application support",
      ];
      return {
        topCbos: cbos.slice(0, 5).map((label) => ({
          label,
          count: intBetween(rng, 22, 140),
        })),
        topGov: gov.slice(0, 4).map((label) => ({
          label,
          count: intBetween(rng, 18, 120),
        })),
      };
    }

    case "quality-measure-projections": {
      return {
        measures: QUALITY_MEASURE_PROJECTION_CARDS.map(card => {
          const current = intBetween(rng, 42, 68);
          const projected = current + intBetween(rng, 2, 14);
          const target = current + intBetween(rng, 6, 12);
          return {
            code: card.code,
            headline: card.headline,
            name: card.name,
            description: card.description,
            current,
            projected,
            target,
            delta: projected - current,
            tracking: null,
            source: 'mock' as const,
          };
        }),
      };
    }

    case "connections-behavioral-care": {
      const referrals = intBetween(rng, 120, 720);
      const connected = Math.round(referrals * (0.6 + rng() * 0.25));
      const noPreEng = 289;
      const eligibleConnected = 94;
      return {
        referrals,
        connected,
        connectionRate: Math.round((connected / referrals) * 100),
        avgDaysToConnect: intBetween(rng, 3, 12),
        noPreEngBhVisit: noPreEng,
        eligibleCostBhConnected: eligibleConnected,
        eligibleCostBhPct: Math.round((eligibleConnected / noPreEng) * 100),
        eligibleCostBhReema: 45,
        insights: buildConnectionsBhCareInsights(connected),
      };
    }

    case "connections-primary-care": {
      return {
        membersSeenByPcp: 326,
        pctEngagedConnectedPcp: 29,
        pctDisconnectedNowInPcp: 32,
        noPreEngPcpVisit: 211,
        eligibleCostPcpConnected: 68,
        eligibleCostPcpPct: 32,
        membersNoPcpOverYear: 211,
        newPcpConnections: 326,
        connectionRate: 29,
        pcpCareTrend: [
          { label: 'Month 1', value: 178 },
          { label: 'Month 2', value: 215 },
          { label: 'Month 3', value: 248 },
          { label: 'Month 4', value: 276 },
          { label: 'Month 5', value: 304 },
          { label: 'Month 6', value: 326 },
        ],
        insights: {
          clinical: {
            heading:
              'Continuity of primary care improves chronic disease management.',
            body: 'For people with multiple chronic conditions, high continuity of primary care is associated with an 84% reduction in the combined risk of ED use and hospitalization.',
            citationLabel: 'Citation',
            citationUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC5556447/',
          },
          financial: {
            heading:
              'Primary care continuity reduces avoidable utilization for the members who drive the most cost.',
            body: 'Medicaid enrollees with chronic conditions who maintain continuous primary care are 28% less likely to visit the ED and 67% less likely to be hospitalized than members without continuity of care.',
            citationLabel: 'Citation',
            citationUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC9793026/',
          },
        },
      };
    }

    case "clinical-care-outcomes": {
      return {
        membersInClinicalCare: 62,
        activeThisMonth: 28,
        treatmentGoalsIdentified: 126,
        months: [
          "Month 1",
          "Month 2",
          "Month 3",
          "Month 4",
          "Month 5",
          "Month 6",
        ],
        phq9Series: [12, 10, 8, 11, 6, 7],
        gad7Series: [10, 9, 8, 9, 5, 7],
        minimalThreshold: 5,
        insights: {
          clinical: {
            heading:
              "Symptom reduction unlocks the functional improvements that drive everything else.",
            body: "Members with depression are 76% more likely to be non-adherent to medications for chronic conditions. As PHQ-9 and GAD-7 scores trend lower, the largest barrier to chronic disease management lifts.",
            citationLabel: "Citation",
            citationUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3181287/",
          },
          financial: {
            heading:
              "Treating depression in members with chronic conditions reduces total healthcare expenditures.",
            body: "For patients with chronic physical conditions and depression, combined antidepressant and psychotherapy treatment is associated with a 28% reduction in total healthcare expenditures compared to no depression treatment.",
            citationLabel: "Citation",
            citationUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4950075/",
          },
        },
      };
    }

    case "bh-medical-diagnoses": {
      return {
        totalNewBhDx: 301,
        totalNewMedicalDx: 490,
        breakdown: [
          { label: "Substance use disorder", count: 105 },
          { label: "Depression", count: 67 },
          { label: "Anxiety", count: 45 },
          { label: "Mood disorders (other)", count: 35 },
          { label: "Bipolar disorder", count: 28 },
          { label: "Psychosis", count: 20 },
        ],
        medical: [
          { label: "Hypertension", count: 316 },
          { label: "High cholesterol", count: 217 },
          { label: "Diabetes", count: 107 },
          { label: "Asthma / COPD", count: 47 },
          { label: "CKD / renal", count: 37 },
          { label: "Heart disease", count: 13 },
        ],
        whyDenominator:
          "These diagnoses reflect only the engaged members with six months of claims runout. As more members reach claims maturity, the denominator grows and these figures will continue to climb.",
        insights: {
          clinical: {
            heading:
              "Diagnosis is the gate to treatment, and treatment changes outcomes.",
            body: "Conditions that have not been diagnosed cannot be treated. Treating hypertension and diabetes reduces all-cause mortality and cardiovascular events by 18%.",
            citationLabel: "Citation",
            citationUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6461269/",
          },
          financial: {
            heading:
              "Undiagnosed conditions accumulate cost. Diagnosis is what stops the curve.",
            body: "Adults with undiagnosed diabetes accumulate an average of $8,941 in additional medical expenditures over 5 years before diagnosis, with costs accelerating each year. Each diagnosis surfaced is a cost curve flattened. More accurate documentation of disease burden also strengthens the risk adjustment your plan relies on.",
            citationLabel: "Citation",
            citationUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7875131/",
          },
        },
      };
    }

    case "harp-enrollment": {
      return {
        statuses: [
          { label: "Undecided", value: intBetween(rng, 30, 60) },
          { label: "Enrolled in HARP", value: intBetween(rng, 10, 25) },
          { label: "Refused", value: intBetween(rng, 6, 14) },
          { label: "In Progress", value: intBetween(rng, 2, 6) },
        ],
        howWeTrack:
          "HARP status is updated by guides after each member conversation. Members move from Undecided to In Progress once they agree to enroll, and to Enrolled in HARP after enrollment is confirmed.",
      };
    }

    case "gap-closures": {
      const screenings = ["A1C", "Dental", "Breast", "Colon", "Kidney"];
      const statuses = ["In Progress", "Scheduled", "Completed", "Refused"];
      const matrix = screenings.map((s) => ({
        screening: s,
        values: statuses.map(() => intBetween(rng, 1, 90)),
      }));
      return {
        statuses,
        matrix,
        howWeTrack:
          "Guides track preventive screening progress for each member. Statuses move from In Progress to Scheduled once an appointment is booked, and to Completed after the screening is confirmed.",
      };
    }

    case "home-visits": {
      const statuses = ["In Program", "Completed", "Refused"];
      const partners = ["MANNA", "Scene"];
      const rows = partners.map((p) => ({
        partner: p,
        values: statuses.map(() => intBetween(rng, 0, 60)),
      }));
      const totals = statuses.map((_, i) =>
        rows.reduce((sum, r) => sum + r.values[i], 0),
      );
      return {
        statuses,
        rows,
        totals,
        howWeTrack:
          "Guides coordinate home visits with MANNA and Scene partners. Members move from In Program to Completed once a home visit is confirmed, or to Refused if the member declines the visit.",
      };
    }

    case "member-spotlight": {
      const snapshot = defaultMemberSpotlightSnapshot();
      return {
        ...snapshot,
        age: 29,
        headline: snapshot.name,
      };
    }

    case "closing-value": {
      return {
        headline: "The Bottom Line",
        summary:
          "Removing barriers and engaging members in care drives total cost of care reduction. Per peer-reviewed research, the average annual savings for initiation into BH outpatient care is $2,000 per member, and the average annual savings for a first PCP visit is $4,000 per member. Applied to Reema's engaged population, the projected savings are below.",
        costReductionRows: [
          {
            leftLabel: "Members w/ no BH visit in last 12 mo",
            leftValue: 360,
            rightLabel: "Members w/ BH visit since Reema",
            rightValue: 137,
            resultLabel: "Cost reduction prediction",
            resultValue: 274_000,
            theme: "bh",
          },
          {
            leftLabel: "Members w/ no PCP visit in last 12 mo",
            leftValue: 211,
            rightLabel: "Members w/ PCP visit since Reema",
            rightValue: 68,
            resultLabel: "Cost reduction prediction",
            resultValue: 272_000,
            theme: "pcp",
          },
        ],
        costReductionMath: [
          { label: "BH connections", value: 274_000 },
          { label: "PCP connections", value: 272_000 },
        ],
        total: 546_000,
        combinedLabel: "Combined projected savings from connection to care alone",
      };
    }

    case "whats-possible-at-maturity": {
      return {
        ...buildWhatsPossibleAtMaturityCopy(customer.name),
        metrics: buildWhatsPossibleAtMaturityMetrics(customer.name),
        note:
          'External, third-party-validated results from a mature Reema deployment — what your program can grow into.',
        citationLabel: 'OBH Wisconsin — full report',
        citationUrl: 'https://www.obhwisconsin.org/outcomes',
      };
    }

    case "looking-ahead": {
      return buildLookingAheadSnapshot({
        customer,
        reportingPeriod,
        engagementRatePct: mockLookingAheadEngagementRatePct(
          customer,
          reportingPeriod,
        ),
        engagementRateSource: 'mock',
      });
    }

    default:
      return {};
  }
}
